import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Trash2, Loader2, AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { projectId } from '../utils/supabase/info';

interface QueueItemsCleanupProps {
  accessToken: string;
}

export function QueueItemsCleanup({ accessToken }: QueueItemsCleanupProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCleanup = async () => {
    if (!confirm('Are you sure you want to delete all corrupt queue items? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/cleanup-corrupt-queue-items`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Error cleaning up queue items:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trash2 className="size-5 text-orange-600" />
          Queue Items Cleanup
        </CardTitle>
        <CardDescription>
          Delete corrupt or incomplete queue items that are missing required fields
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This will permanently delete queue items that are missing:
            <ul className="list-disc list-inside mt-2 text-sm">
              <li>Title</li>
              <li>Description</li>
              <li>Company ID/Name</li>
              <li>Created date</li>
              <li>Priority</li>
            </ul>
          </AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Error: {error}
            </AlertDescription>
          </Alert>
        )}

        {result && (
          <Alert className={result.deleted > 0 ? "border-green-200 bg-green-50" : "border-blue-200 bg-blue-50"}>
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">{result.message}</p>
                <div className="text-sm text-muted-foreground">
                  <p>Total queue items: {result.total}</p>
                  <p>Corrupt items found: {result.corrupt}</p>
                  <p>Successfully deleted: {result.deleted}</p>
                  <p>Remaining valid items: {result.remaining}</p>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Button
          onClick={handleCleanup}
          disabled={loading}
          variant="destructive"
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="size-4 mr-2 animate-spin" />
              Cleaning Up...
            </>
          ) : (
            <>
              <Trash2 className="size-4 mr-2" />
              Delete Corrupt Queue Items
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
